// Sortable data table. Header buttons sort; aria-sort reflects the state.

import { h, type Child } from './dom';
import { infoTip, type HelpEntry } from './help';

export type SortDir = 'asc' | 'desc';
export type SortValue = number | string | null;

export interface Column<R> {
  key: string;
  label: string;
  /** Longer explanation, shown as the header's tooltip and in the caption. */
  title?: string;
  /** A definition opened by a "?" button next to the header. */
  help?: HelpEntry;
  numeric?: boolean;
  /** Direction used on the first click. Defaults to desc for numbers, asc for text. */
  firstDir?: SortDir;
  sortable?: boolean;
  /** Extra class for the cells, e.g. 'col-model' (sticky first column). */
  cls?: string;
  /** Value used for sorting; null always sorts last. */
  value: (row: R) => SortValue;
  render: (row: R) => Child;
}

export interface RowDetail<R> {
  /** Column whose cells get the toggle button, next to their content. */
  column: string;
  /** The button's visible text. */
  label: string;
  /** The button's accessible name; starts with `label`. */
  describe: (row: R) => string;
  /** Builds the line's content on first open; it is kept, so sorting or closing doesn't rebuild it. */
  render: (row: R) => Node;
}

export interface TableOptions<R> {
  caption: string;
  columns: Column<R>[];
  rows: R[];
  /** Order used before any header is clicked (and as the tie-breaker). */
  defaultOrder?: (a: R, b: R) => number;
  /** Column shown as sorted initially (purely presentational with defaultOrder). */
  initialSort?: { key: string; dir: SortDir };
  rowClass?: (row: R) => string | undefined;
  /** Column whose cells are row headers (<th scope="row">). Defaults to the first column. */
  rowHeaderKey?: string;
  /** A full-width line under each row, opened by a toggle button. */
  detail?: RowDetail<R>;
}

let detailIds = 0;

function compareValues(a: SortValue, b: SortValue, dir: SortDir): number {
  if (a === null && b === null) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  const cmp = typeof a === 'number' && typeof b === 'number' ? a - b : String(a).localeCompare(String(b), undefined, { sensitivity: 'base' });
  return dir === 'asc' ? cmp : -cmp;
}

export function sortableTable<R>(opts: TableOptions<R>): HTMLElement {
  const tbody = h('tbody');
  const headers = new Map<string, HTMLTableCellElement>();
  let sort: { key: string; dir: SortDir } | null = opts.initialSort ?? null;
  let custom = false; // false while showing the default order
  const rowHeaderKey = opts.rowHeaderKey ?? opts.columns[0]?.key;
  const anySortable = opts.columns.some((c) => c.sortable !== false);

  const baseOrder = opts.defaultOrder ? [...opts.rows].sort(opts.defaultOrder) : [...opts.rows];

  const open = new Set<R>();
  const details = new Map<R, HTMLTableRowElement>();

  const detailRow = (row: R, detail: RowDetail<R>): HTMLTableRowElement => {
    let line = details.get(row);
    if (!line) {
      line = h(
        'tr',
        { class: 'detail-row', attrs: { id: `row-detail-${++detailIds}` } },
        h('td', { attrs: { colspan: opts.columns.length } }, h('div', { class: 'detail-box' }, detail.render(row))),
      );
      details.set(row, line);
    }
    return line;
  };

  const toggle = (row: R, tr: HTMLTableRowElement, detail: RowDetail<R>): HTMLButtonElement => {
    const btn = h(
      'button',
      { class: 'row-toggle', attrs: { type: 'button', 'aria-label': detail.describe(row) } },
      detail.label,
      h('span', { class: 'row-toggle-arrow', attrs: { 'aria-hidden': 'true' } }),
    );
    const sync = (): void => {
      btn.setAttribute('aria-expanded', String(open.has(row)));
      if (open.has(row)) btn.setAttribute('aria-controls', detailRow(row, detail).id);
    };
    btn.addEventListener('click', () => {
      if (open.has(row)) {
        open.delete(row);
        details.get(row)?.remove();
      } else {
        open.add(row);
        tr.after(detailRow(row, detail));
      }
      sync();
    });
    sync();
    return btn;
  };

  const renderBody = (): void => {
    let rows = baseOrder;
    if (custom && sort) {
      const col = opts.columns.find((c) => c.key === sort?.key);
      if (col) {
        const dir = sort.dir;
        const index = new Map(baseOrder.map((r, i) => [r, i]));
        rows = [...baseOrder].sort((a, b) => compareValues(col.value(a), col.value(b), dir) || (index.get(a) ?? 0) - (index.get(b) ?? 0));
      }
    }
    const detail = opts.detail;
    const lines: HTMLTableRowElement[] = [];
    for (const row of rows) {
      const tr = h('tr', { class: opts.rowClass?.(row) });
      for (const c of opts.columns) {
        const cls = [c.numeric ? 'num' : '', c.cls ?? ''].join(' ').trim() || undefined;
        const content = detail && c.key === detail.column ? h('span', { class: 'cell-toggle' }, c.render(row), toggle(row, tr, detail)) : c.render(row);
        tr.append(c.key === rowHeaderKey ? h('th', { class: cls, attrs: { scope: 'row' } }, content) : h('td', { class: cls }, content));
      }
      lines.push(tr);
      if (detail && open.has(row)) lines.push(detailRow(row, detail));
    }
    tbody.replaceChildren(...lines);
    for (const [key, th] of headers) {
      const active = sort?.key === key;
      th.setAttribute('aria-sort', active && sort ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none');
      th.classList.toggle('sorted', active);
    }
  };

  const onSort = (col: Column<R>): void => {
    const first: SortDir = col.firstDir ?? (col.numeric ? 'desc' : 'asc');
    if (sort?.key === col.key) sort = { key: col.key, dir: sort.dir === 'asc' ? 'desc' : 'asc' };
    else sort = { key: col.key, dir: first };
    custom = true;
    renderBody();
  };

  const headRow = h(
    'tr',
    null,
    ...opts.columns.map((col) => {
      const cls = [col.numeric ? 'num' : '', col.cls ?? ''].join(' ').trim() || undefined;
      const th = h('th', { class: cls, attrs: { scope: 'col', title: col.title } });
      if (col.sortable === false) {
        th.append(col.label);
        if (col.help) th.append(infoTip(col.help));
      } else {
        th.append(
          h(
            'button',
            { class: 'sort', attrs: { type: 'button' }, on: { click: () => onSort(col) } },
            h('span', null, col.label),
            h('span', { class: 'sort-arrow', attrs: { 'aria-hidden': 'true' } }),
          ),
        );
        if (col.help) th.append(infoTip(col.help));
        headers.set(col.key, th);
      }
      return th;
    }),
  );

  const table = h(
    'table',
    { class: 'data' },
    h('caption', { class: 'sr-only' }, anySortable ? `${opts.caption}. Column headers are buttons that sort the table.` : opts.caption),
    h('thead', null, headRow),
    tbody,
  );
  renderBody();
  // A detail line spans the whole table, which can be wider than the screen: it sizes itself to the wrapper.
  return h('div', { class: opts.detail ? 'table-wrap table-wrap--details' : 'table-wrap', attrs: { tabindex: '0', role: 'region', 'aria-label': opts.caption } }, table);
}
